import { useCallback, useEffect, useRef } from 'react';

/**
 * SlotText — hover to shuffle characters left-to-right like a slot machine.
 * Supports English letters/numbers AND Japanese kana/kanji.
 * `color` sets the base (default) text color applied before and after shuffling.
 * `scrambleColor` overrides the color while letters are shuffling.
 *   - Pass a color string (e.g. "oklch(...)") for a single scramble color.
 *   - Pass "rainbow" to cycle each character through a fast hue rainbow.
 * `speed` controls the reel pace: "slow" (default) or "fast".
 */
const GLYPHS_EN = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const GLYPHS_HIRA = 'あいうえおかきくけこさしすせそたちつてとなにぬねのはひふへほまみむめもやゆよらりるれろわをん';
const GLYPHS_KATA = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
const GLYPHS_KANJI = '戦略経験価値設計課題解決創造実装環境認知行動美機能統合本質出発生活者視点組織価値挑戦未来理解';

function glyphSetFor(ch: string): string | null {
  if (/[A-Za-z0-9]/.test(ch)) return GLYPHS_EN;
  if (/[\u3040-\u309f]/.test(ch)) return GLYPHS_HIRA;
  if (/[\u30a0-\u30ff]/.test(ch)) return GLYPHS_KATA;
  if (/[\u4e00-\u9fff]/.test(ch)) return GLYPHS_KANJI;
  return null;
}

function randomGlyph(set: string): string {
  return set[Math.floor(Math.random() * set.length)];
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

interface SlotTextProps {
  text: string;
  className?: string;
  as?: 'span' | 'h1' | 'h2' | 'h3' | 'p' | 'div';
  /** Base (default) text color, applied before and after shuffling. */
  color?: string;
  /** Color applied to letters while shuffling. Defaults to the base color or inherited color. Pass "rainbow" for rainbow cycling. */
  scrambleColor?: string;
  /** Reel pace. "fast" is quicker and snappier (used for rainbow archive cards). */
  speed?: 'slow' | 'fast';
}

export default function SlotText({
  text,
  className = '',
  as = 'span',
  color,
  scrambleColor,
  speed = 'slow',
}: SlotTextProps) {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const frame = useRef(0);

  const isRainbow = scrambleColor === 'rainbow';
  const TOTAL_FRAMES = speed === 'fast' ? 12 : 28;
  const STEP_MS = speed === 'fast' ? 28 : 55;

  const stop = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => () => stop(), [stop]);

  const renderPlain = useCallback(
    (done: boolean): string => {
      let out = '';
      for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        const set = glyphSetFor(ch);
        if (!set) {
          out += ch;
          continue;
        }
        if (done) {
          out += ch;
          continue;
        }
        const settleAt = Math.round((i / Math.max(text.length - 1, 1)) * (TOTAL_FRAMES - 4));
        out += frame.current < settleAt ? randomGlyph(set) : ch;
      }
      return out;
    },
    [text, TOTAL_FRAMES],
  );

  const renderRainbow = useCallback(
    (done: boolean): string => {
      let html = '';
      for (let i = 0; i < text.length; i += 1) {
        const ch = text[i];
        const set = glyphSetFor(ch);
        if (!set) {
          html += escapeHtml(ch);
          continue;
        }
        let outChar = ch;
        if (!done) {
          const settleAt = Math.round((i / Math.max(text.length - 1, 1)) * (TOTAL_FRAMES - 4));
          if (frame.current < settleAt) outChar = randomGlyph(set);
        }
        const hue = (i * 47 + frame.current * 12) % 360;
        html += `<span style="color:hsl(${hue},70%,58%)">${outChar}</span>`;
      }
      return html;
    },
    [text, TOTAL_FRAMES],
  );

  const scramble = useCallback(() => {
    stop();
    frame.current = 0;
    const el = textRef.current;
    if (!el) return;

    if (!isRainbow) el.style.color = scrambleColor ?? color ?? '';

    timer.current = setInterval(() => {
      frame.current += 1;
      const done = frame.current >= TOTAL_FRAMES;
      if (isRainbow) {
        el.innerHTML = renderRainbow(done);
      } else {
        el.textContent = renderPlain(done);
      }
      if (done) {
        el.textContent = text;
        if (!isRainbow) el.style.color = color ?? '';
        stop();
      }
    }, STEP_MS);
  }, [text, color, scrambleColor, isRainbow, TOTAL_FRAMES, STEP_MS, renderPlain, renderRainbow, stop]);

  const reset = useCallback(() => {
    stop();
    const el = textRef.current;
    if (!el) return;
    el.textContent = text;
    el.style.color = color ?? '';
  }, [text, color, stop]);

  const Tag = as;

  return (
    <Tag className={className} onMouseEnter={scramble} onMouseLeave={reset}>
      <span ref={textRef} style={color ? { color } : undefined}>
        {text}
      </span>
    </Tag>
  );
}